import React from 'react';
import { Paperclip, Pencil, Plus, Power, PowerOff, RefreshCw, Trash2 } from 'lucide-react';

import { CardAction, EmptyState, PhonePage, PillButton, ScreenLoader, StatusPill, TopBar } from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * Attachment categories, on the admin's phone: one card per category saying
 * who may upload to it, how many files sit under it and whether it is live,
 * with the row's three actions underneath.
 */
export default function PhoneAttachmentCategories({
  categories, loading, roleLabel, onBack, onAdd, onRefresh, onEdit, onToggleActive, onDelete,
}) {
  return (
    <PhonePage testID="attachment-categories-page">
      <TopBar
        eyebrow="SETTINGS"
        title="Attachment Categories"
        subtitle={`${categories.length} · ${categories.filter((c) => c.is_active).length} active`}
        onBack={onBack}
        actions={(
          <PillButton onClick={onAdd} testID="add-category-btn">
            <Plus className="h-4 w-4" /> Add
          </PillButton>
        )}
      />

      <div className="flex-1 px-5 pb-8">
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
            What files can be filed under, and who may upload them
          </span>
          <button
            type="button"
            onClick={onRefresh}
            className="flex h-10 shrink-0 items-center gap-1 text-[12px] font-semibold"
            style={{ color: Colors.accent.DEFAULT }}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {loading ? (
          <ScreenLoader />
        ) : categories.length === 0 ? (
          <EmptyState icon={Paperclip} message="No categories yet" hint="Add one so files have somewhere to go." />
        ) : (
          <ul data-testid="attachment-categories-list">
            {categories.map((category) => (
              <li
                key={category.id}
                data-testid={`category-card-${category.id}`}
                className="mt-3 rounded-[10px] border"
                style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
              >
                <button type="button" onClick={() => onEdit(category)} className="block w-full px-4 pt-3 text-left">
                  <span className="flex items-center gap-2">
                    <span className="h-3 w-3 shrink-0 rounded-full border" style={{ backgroundColor: category.color_hex, borderColor: Colors.border.default }} />
                    <span
                      className="truncate text-[15px] font-bold -tracking-[0.2px]"
                      style={{ color: category.is_active ? Colors.text.primary : Colors.text.tertiary }}
                    >
                      {category.name}
                    </span>
                  </span>
                  <span className="mt-1 block text-[12px]" style={{ color: Colors.text.secondary }}>
                    {category.allowed_roles?.length
                      ? category.allowed_roles.map(roleLabel).join(', ')
                      : 'Everyone can upload'}
                  </span>
                  <span className="flex flex-wrap items-center gap-2 py-3">
                    <StatusPill
                      label={category.is_active ? 'Active' : 'Inactive'}
                      tone={category.is_active ? 'positive' : 'neutral'}
                      dot
                    />
                    <span className="text-[12px]" style={{ color: Colors.text.secondary }}>
                      {category.attachment_count ?? 0} file{category.attachment_count === 1 ? '' : 's'}
                    </span>
                  </span>
                </button>
                <div className="flex items-center gap-2 border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
                  <CardAction icon={Pencil} label="Edit" onClick={() => onEdit(category)} testID={`category-edit-${category.id}`} />
                  <CardAction
                    icon={category.is_active ? PowerOff : Power}
                    label={category.is_active ? 'Deactivate' : 'Activate'}
                    tone={category.is_active ? 'caution' : 'positive'}
                    onClick={() => onToggleActive(category)}
                    testID={`category-toggle-${category.id}`}
                  />
                  <CardAction icon={Trash2} label="Delete" tone="critical" onClick={() => onDelete(category)}
                    className="ml-auto" testID={`category-delete-${category.id}`} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PhonePage>
  );
}
